import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  line-height: 1.8;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 30px;
  color: var(--color-black);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 40px 0 20px 0;
  color: var(--color-black);
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: var(--color-black);
  margin-bottom: 20px;
  text-align: justify;
  
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const List = styled.ul`
  font-size: 1rem;
  color: var(--color-black);
  margin-bottom: 20px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 10px;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 25px 0 15px 0;
  color: var(--color-black);
`;

const ContactInfo = styled.p`
  font-size: 1rem;
  color: var(--color-black);
  margin: 20px 0;
  font-weight: 600;
`;

const PrivacyScreen: React.FC = () => {
  return (
    <Container>
      <Title>Privacy Policy</Title>
      
      <SectionTitle>1. Introduction</SectionTitle>
      
      <Paragraph>
        Welcome to Accord! This Privacy Policy explains how we ("the Accord Team," "we," "us," or "our") handle your information when you use our mobile application ("Service").
      </Paragraph>
      
      <Paragraph>
        Our philosophy is simple: we only collect the absolute minimum information required to provide and improve our Service. Your privacy is a top priority. By using Accord, you agree to the collection and use of information in accordance with this policy.
      </Paragraph>
      
      <SectionTitle>2. Information We Collect</SectionTitle>
      
      <Paragraph>
        We have designed our systems to be as private as possible. We only collect information that you voluntarily provide to us.
      </Paragraph>
      
      <SubSectionTitle>a) Information You Provide Directly:</SubSectionTitle>
      
      <Paragraph>
        <strong>Account Information:</strong> When you create an account, either through email or a third-party service like Google, we collect basic information to identify you, such as your name and email address.
      </Paragraph>
      
      <Paragraph>
        <strong>Photos for Wallpaper Generation:</strong> We collect the single photo of your phone case that you choose to upload. This is essential for our AI to analyze and generate your custom wallpaper. We do not access, scan, or view any other photos or files on your device.
      </Paragraph>
      
      <Paragraph>
        <strong>Purchase Information:</strong> If you make a purchase, it is processed securely by a third-party payment processor (e.g., Google Play). We receive a confirmation or receipt of the transaction to grant you access to paid features, but we do not collect, receive, or store your credit card number or any other sensitive financial details.
      </Paragraph>
      
      <SubSectionTitle>b) Information We DO NOT Collect:</SubSectionTitle>
      
      <Paragraph>
        We believe in data minimization. To protect your privacy, we do not automatically collect or track personal data from your device. This includes:
      </Paragraph>
      
      <List>
        <ListItem>Device Information (e.g., device model, OS version, unique device IDs)</ListItem>
        <ListItem>Usage Analytics (e.g., how often you use the app, which buttons you press)</ListItem>
        <ListItem>Your IP Address</ListItem>
        <ListItem>Your Location Data</ListItem>
      </List>
      
      <SectionTitle>3. How We Use Your Information</SectionTitle>
      
      <Paragraph>
        We use the limited information we collect for the following specific purposes:
      </Paragraph>
      
      <Paragraph>
        <strong>To Provide the Core Service:</strong> We use the photo you upload to analyze its aesthetics and generate a matching AI wallpaper. This is the sole purpose of the photo.
      </Paragraph>
      
      <Paragraph>
        <strong>To Manage Your Account:</strong> We use your account information to create your account, provide you with access to your generated wallpaper history, and for communication purposes if needed.
      </Paragraph>
      
      <Paragraph>
        <strong>To Process Transactions:</strong> We use the purchase confirmation to validate your payments and update your account status.
      </Paragraph>
      
      <SectionTitle>4. How We Share Your Information</SectionTitle>
      
      <Paragraph>
        We do not sell, trade, or rent your personal information to others. We only share your data with trusted third-party service providers who help us operate our Service, and only to the extent necessary.
      </Paragraph>
      
      <Paragraph>
        <strong>AI Service Providers:</strong> To generate your wallpaper, your uploaded photo is securely sent to our third-party AI partners for processing. They are contractually obligated to use the photo only for this task and to not store it after the process is complete.
      </Paragraph>
      
      <Paragraph>
        <strong>Cloud Hosting Providers:</strong> Our application and your data (such as your account information and generated wallpapers) are stored on secure servers managed by reputable third-party cloud hosting providers.
      </Paragraph>
      
      <Paragraph>
        <strong>Payment Processors:</strong> All payments are handled by secure third-party payment processors like Google Play. We only share the information necessary to complete the transaction.
      </Paragraph>
      
      <SectionTitle>5. Data Retention and Deletion</SectionTitle>
      
      <Paragraph>
        Your control over your data is paramount.
      </Paragraph>
      
      <Paragraph>
        <strong>Uploaded Photos:</strong> The photo of your phone case that you upload is permanently deleted from our servers immediately after the AI wallpaper has been successfully generated. We do not keep it.
      </Paragraph>
      
      <Paragraph>
        <strong>Generated Wallpapers:</strong> The final wallpapers are saved to your account history for your convenience. You have full control to delete any wallpaper from your history at any time through the app.
      </Paragraph>
      
      <Paragraph>
        <strong>Account Deletion:</strong> You can request the deletion of your entire account by contacting us. Upon your request, we will permanently delete your account information and your entire wallpaper history.
      </Paragraph>
      
      <SectionTitle>6. Data Security</SectionTitle>
      
      <Paragraph>
        We are committed to protecting your information. We use commercially reasonable administrative, technical, and physical safeguards to prevent unauthorized access, use, or disclosure of your information. However, no method of transmission over the Internet or electronic storage is 100% secure.
      </Paragraph>
      
      <SectionTitle>7. Children's Privacy</SectionTitle>
      
      <Paragraph>
        Our Service is not intended for use by children under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If we become aware that we have collected such information, we will take steps to delete it immediately.
      </Paragraph>
      
      <SectionTitle>8. International Data Transfers</SectionTitle>
      
      <Paragraph>
        We are based in Sydney, Australia, but our third-party service providers may operate in other countries. Therefore, your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ. We will take all steps reasonably necessary to ensure that your data is treated securely.
      </Paragraph>
      
      <SectionTitle>9. Changes to This Privacy Policy</SectionTitle>
      
      <Paragraph>
        We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy within our application. You are advised to review this Privacy Policy periodically for any changes.
      </Paragraph>
      
      <SectionTitle>10. Contact Us</SectionTitle>
      
      <ContactInfo>
        If you have any questions about this Privacy Policy, please contact us at: hello@accordwp.com
      </ContactInfo>
    </Container>
  );
};

export default PrivacyScreen;
